
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/services" },
    { name: "POS Systems & Equipment", href: "/products" },
    { name: "About Us", href: "/about" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none"
      >
        Skip to main content
      </a>
      <header className="bg-background/95 backdrop-blur-md shadow-sm border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover-scale">
            <img 
              src="/processing-bros-logo-dark.png" 
              className="w-[120px] sm:w-[150px] md:w-[180px] h-auto transition-all duration-300 block dark:hidden"
              alt="Processing Bros - Your Success, Our Process" 
              width="180"
              height="40"
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
            <img 
              src="/processing-bros-logo-light.png" 
              className="w-[120px] sm:w-[150px] md:w-[180px] h-auto transition-all duration-300 hidden dark:block"
              alt="Processing Bros - Your Success, Our Process" 
              width="180"
              height="40"
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-all duration-300 hover:text-primary relative group ${
                  isActive(item.href) 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full ${
                  isActive(item.href) ? 'w-full' : ''
                }`}></span>
              </Link>
            ))}
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden md:flex items-center space-x-6">
            <a 
              href="tel:9543992230" 
              className="flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-300 hover-scale"
            >
              <Phone className="h-4 w-4 mr-2" />
              (954) 399-2230
            </a>
            <Button variant="default" asChild>
              <a 
                href="https://merchantech.com/app/bradley-brown/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border animate-fade-in">
            <div className="py-6 space-y-1 bg-background/95 backdrop-blur-md">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-6 py-3 text-sm font-medium transition-all duration-300 rounded-lg mx-4 ${
                    isActive(item.href) 
                      ? 'text-primary bg-primary-muted' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="px-4 pt-6 border-t border-border mt-4 space-y-4">
                <a 
                  href="tel:9543992230" 
                  className="flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  (954) 399-2230
                </a>
                <Button className="w-full" asChild>
                  <a 
                    href="https://merchantech.com/app/bradley-brown/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Apply Now
                  </a>
                </Button>
              </div>
            </div>
          </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
